import { Component, inject, type OnInit, viewChild } from '@angular/core';
import { AzureAuthService, DtisAzureRoles, DtisSplashscreenComponent, type RailBarRouteConfig } from '@dtis/angular';
import { dispatch, select } from '@ngxs/store';
import { DtisShellComponent } from '../components/dtis-shell/dtis-shell.component';
import { environment } from '../environments/environment';
import { LoggingService } from '../services/logging.service';
import { ApplyLanguage, SetAdminMode, ToggleDebugMode } from './states/settings/settings.actions';
import { SettingsState } from './states/settings/settings.state';

/**
 * Root component of the DTIS application.
 */
@Component({
  selector: 'dtis-root',
  imports: [DtisShellComponent, DtisSplashscreenComponent],
  template: `
    <dtis-splashscreen [maxDurationMs]="durationSplashScreen" />
    <dtis-shell
      (toggleDebug)="toggleDebug()"
      [railConfig]="railConfig"
      [title]="'DTIS'"
      [appVersion]="appVersion"
    />
  `,
})
export class AppComponent implements OnInit {
  /**
   * Logging service for application insights and logging.
   */
  private readonly logging = inject(LoggingService);
  private readonly auth = inject(AzureAuthService);
  private readonly shell = viewChild.required(DtisShellComponent);

  readonly durationSplashScreen = environment.e2e ? 0 : 3500;

  /**
   * Application version from the environment configuration.
   */
  appVersion = environment.version;

  /**
   * Observable to determine if the current user has admin privileges.
   */
  isAdmin$ = select(SettingsState.isAdmin);

  /**
   * Dispatch action to toggle debug mode.
   */
  toggleDebug = dispatch(ToggleDebugMode);

  /**
   * Dispatch action to apply the selected language.
   */
  applyLanguage = dispatch(ApplyLanguage);

  /**
   * Dispatch action to set admin mode.
   */
  setAdminMode = dispatch(SetAdminMode);
  /**
   *
   */
  railConfig: RailBarRouteConfig = {
    routes: [
      {
        name: 'home',
        icon: 'home',
        route: ['/'],
        exactMatch: true,
      },
      {
        name: 'LANDING.VIEW_LIST.views_title',
        icon: 'show_chart',
        onClick: () => this.shell().toggleViewsDrawer(),
      },
      {
        icon: 'layers',
        route: ['/telemetry'],
        name: 'telemetry',
      },
      {
        icon: 'device_hub',
        route: ['/iot-status'],
        name: 'iot-status',
      },
      {
        icon: 'troubleshoot',
        route: ['/anomalies'],
        name: 'anomalies',
      },
    ],
  };

  /**
   * Constructor initializes the account, tracks user ID if present,
   * and sets admin mode based on user roles.
   */
  ngOnInit() {
    this.trackUserIdIfPresent();
    this.setAdminMode(this.auth.hasRole(DtisAzureRoles.SUPER_USER));
    this.applyLanguage();
    // attach admin mode to the footer version
    this.appVersion = `${environment.version}`;
  }

  /**
   * Tracks the user ID in application insights if it is present.
   */
  private trackUserIdIfPresent() {
    const localUserId = this.auth.handleAccount()?.localAccountId;
    if (localUserId) {
      this.logging.appInsights.setAuthenticatedUserContext(localUserId);
    }
  }
}
